import { type FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Radio from '@/components/UI/FormElements/Radio/Radio';
import { preventNumberScroll } from '@/helpers';
import { _notNil } from '@/littledash';
import type { ObservationRowData } from './RecordBulkObservations.utils';
import styles from './ObservationEntryCard.module.scss';

interface ObservationEntryCardProps {
  index: number;
  observationData: ObservationRowData;
  testId?: string;
}

const SEVERITY_OPTIONS = ['Mild', 'Moderate', 'Severe'] as const;

const ObservationEntryCard: FC<ObservationEntryCardProps> = ({
  index,
  observationData,
  testId = 'observation-entry-card',
}) => {
  const { control } = useFormContext();

  const hasRange = _notNil(observationData.options?.min) || _notNil(observationData.options?.max);
  const rangeLabel = hasRange ? `(${observationData.options?.min}-${observationData.options?.max})` : '';

  return (
    <div
      className={styles.card}
      data-test-component="ObservationEntryCard"
      data-test-element="container"
      data-test-key={observationData.team_glossary_api_id}
      data-testid={testId}
    >
      <div className={styles.content}>
        <div className={styles.titleSection}>
          <h4
            className={styles.title}
            data-test-element="title"
            data-tooltip-id="global-tooltip-id"
            data-tooltip-content={observationData.description}
          >
            {observationData.observationType}
          </h4>
          {_notNil(observationData.description) && (
            <p className={styles.description} data-test-element="description">
              {observationData.description}
            </p>
          )}
        </div>

        <div className={styles.inputSection}>
          {observationData.type === 'numeric' && (
            <div className={styles.inputGroup} data-test-element="numeric-input-group">
              <label htmlFor={`observations.${index}.score`}>Score {rangeLabel}</label>
              <Controller
                control={control}
                name={`observations.${index}.score`}
                rules={{
                  required: 'Score is required',
                  validate: (value) => {
                    if (hasRange) {
                      const numValue = Number(value);
                      const min = Number(observationData.options?.min);
                      const max = Number(observationData.options?.max);
                      if (!isNaN(min) && numValue < min) {
                        return `Score must be at least ${min}`;
                      }
                      if (!isNaN(max) && numValue > max) {
                        return `Score must be at most ${max}`;
                      }
                    }
                    return true;
                  },
                }}
                render={({ field, fieldState }) => (
                  <>
                    <input
                      {...field}
                      type="number"
                      id={`observations.${index}.score`}
                      className={fieldState.invalid ? 'ui__error' : ''}
                      style={{ width: '120px', marginBottom: 0 }}
                      min={observationData.options?.min}
                      max={observationData.options?.max}
                      onWheel={preventNumberScroll}
                      data-test-element="score-input"
                      data-testid={`${testId}__score-input`}
                    />
                    {_notNil(fieldState.error?.message) && (
                      <p className="f6 red mt1" data-test-element="score-error">
                        {fieldState.error.message}
                      </p>
                    )}
                  </>
                )}
              />
            </div>
          )}

          {observationData.type === 'severity' && (
            <div className={styles.inputGroup} data-test-element="severity-input-group">
              <label>Severity</label>
              <Controller
                control={control}
                name={`observations.${index}.severity`}
                rules={{ required: 'Severity is required' }}
                render={({ field, fieldState }) => (
                  <>
                    <div className={styles.radioGroup}>
                      {SEVERITY_OPTIONS.map((severity) => (
                        <Radio
                          key={severity}
                          id={`observations.${index}.severity.${severity}`}
                          name={`observations.${index}.severity`}
                          value={severity}
                          label={severity}
                          checked={field.value === severity}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="mr3"
                          dataTestId={`${testId}__severity-${severity.toLowerCase()}`}
                        />
                      ))}
                    </div>
                    {_notNil(fieldState.error?.message) && (
                      <p className="f6 red mt1" data-test-element="severity-error">
                        {fieldState.error.message}
                      </p>
                    )}
                  </>
                )}
              />
            </div>
          )}

          <div className={styles.inputGroup} data-test-element="comments-input-group">
            <label htmlFor={`observations.${index}.comments`}>Additional comments</label>
            <Controller
              control={control}
              name={`observations.${index}.comments`}
              render={({ field }) => (
                <textarea
                  {...field}
                  id={`observations.${index}.comments`}
                  maxLength={5000}
                  rows={3}
                  className={styles.textarea}
                  data-test-element="comments-input"
                  data-testid={`${testId}__comments-input`}
                />
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ObservationEntryCard;
